// @flow
import type { User } from '../modules/user';
import type { Action, State as TopLevelState } from '../store';
import * as actions from '../actions/new-order';
import * as at from '../actions/action-types/new-order';
import { byKey } from '../modules/abstract-catalog';
import type { Customer } from '../modules/customer';
import type {
  AbstractCatalog,
  AbstractCatalogFilter,
} from '../modules/abstract-catalog';
import { customerByNumber, catalogsForCustomer } from '../modules/user';
import type { TabularOrder } from '../modules/tabular-order';

export type State = $ReadOnly<{|
  selectedCustomer: ?Customer,
  exportAvailabilityOpen: boolean,
  detailsOpen: boolean,
  catalogTypeFilter: AbstractCatalogFilter,
  keywordFilter: string,
  catalogForNewOrder: ?AbstractCatalog,
  catalogForExportAvailability: ?AbstractCatalog,
  offlineOrderForm: TabularOrder | null,
  offlineOrderFormError: boolean,
|}>;

export const localState = (state: TopLevelState): State => state.newOrder;

export const selectors = {
  selectedCustomer: (state: TopLevelState): ?Customer =>
    localState(state).selectedCustomer,

  keywordFilter: (state: TopLevelState): string =>
    localState(state).keywordFilter,

  catalogTypeFilter: (state: TopLevelState): AbstractCatalogFilter =>
    localState(state).catalogTypeFilter,

  catalogForNewOrder: (state: TopLevelState): ?AbstractCatalog =>
    localState(state).catalogForNewOrder,

  catalogForExportAvailability: (state: TopLevelState): ?AbstractCatalog =>
    localState(state).catalogForExportAvailability,

  offlineOrderFormError: (state: TopLevelState): boolean =>
    localState(state).offlineOrderFormError,

  offlineOrderForm: (state: TopLevelState): ?TabularOrder =>
    localState(state).offlineOrderForm,

  exportAvailabilityOpen: (state: TopLevelState): boolean =>
    localState(state).exportAvailabilityOpen,

  detailsOpen: (state: TopLevelState): boolean => localState(state).detailsOpen,
};

export const initialState: State = {
  selectedCustomer: null,
  exportAvailabilityOpen: false,
  detailsOpen: false,
  catalogTypeFilter: 'standard_catalogs',
  keywordFilter: '',
  catalogForNewOrder: null,
  catalogForExportAvailability: null,
  offlineOrderForm: null,
  offlineOrderFormError: false,
};

// Action Types are defined for every case
const newOrder = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case at.NEW_ORDER_DETAILS_OPEN:
      return {
        ...state,
        catalogForNewOrder: (action: actions.DetailsOpen).payload,
      };
    case at.NEW_ORDER_DETAILS_CLOSE:
      return {
        ...state,
        catalogForNewOrder: null,
      };
    case at.NEW_ORDER_EXPORT_AVAILABILITY_OPEN:
      return {
        ...state,
        catalogForExportAvailability: (action: actions.ExportAvailabilityOpen)
          .payload,
      };
    case at.NEW_ORDER_EXPORT_AVAILABILITY_CLOSE:
      return {
        ...state,
        catalogForExportAvailability: null,
      };
    case at.NEW_ORDER_CUSTOMER_SET:
      return ((a: actions.CustomerSet) => ({
        ...state,
        selectedCustomer: a.payload,
      }))(action);
    case at.NEW_ORDER_KEYWORD_FILTER_SET:
      return {
        ...state,
        keywordFilter: (action: actions.KeywordFilterSet).payload,
      };
    case at.NEW_ORDER_CATALOG_TYPE_FILTER_SET:
      return {
        ...state,
        catalogTypeFilter: (action: actions.CatalogTypeFilterSet).payload,
      };
    case at.NEW_ORDER_OFFLINE_ORDER_FORM_RECEIVE:
      return (({
        offlineOrderForm,
        user,
      }: {|
        +offlineOrderForm: TabularOrder,
        +user: User,
      |}) => {
        const customer =
          (offlineOrderForm.customerNumber &&
            customerByNumber(user, offlineOrderForm.customerNumber)) ||
          state.selectedCustomer;

        const catalogFromOlof =
          (customer &&
            offlineOrderForm.catalogKey &&
            byKey(
              catalogsForCustomer(
                user,
                customer,
                ((user.catalogs: any): AbstractCatalog[]),
              ),
              ((offlineOrderForm.catalogKey: any): string),
            )) ||
          state.catalogForNewOrder;

        return {
          ...state,
          selectedCustomer: customer,
          catalogForNewOrder: catalogFromOlof,
          offlineOrderForm,
          offlineOrderFormError: false,
          detailsOpen: !!catalogFromOlof,
        };
      })(action.payload);
    case at.NEW_ORDER_OFFLINE_ORDER_FORM_FAIL_TO_RECEIVE:
      return {
        ...state,
        offlineOrderFormError: true,
      };

    case at.NEW_ORDER_OFFLINE_ORDER_FORM_CLEAR:
      return {
        ...state,
        offlineOrderForm: null,
        offlineOrderFormError: false,
      };
    case at.NEW_ORDER_RESET:
      return initialState;
    default:
      return state;
  }
};

export default newOrder;
